import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose, type }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  // Normalize URL fields because backend rows may use snake_case (github_url, live_url)
  const repoUrl = project?.githubUrl || project?.github_url || project?.github || null;
  const demoUrl = project?.demoUrl || project?.demo_url || project?.live_url || project?.liveUrl || null;

  // Helper to ensure URLs are absolute. If user stored 'username' or 'username/repo' or 'github.com/username',
  // convert to a full https://github.com/... URL. Also leaves https:// links intact.
  const normalizeRepoUrl = (url) => {
    if (!url) return null;
    const trimmed = String(url).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (/^github\.com\//i.test(trimmed)) return `https://${trimmed}`;
    if (/^[^/]+\/[^/]+$/.test(trimmed)) return `https://github.com/${trimmed}`; // username/repo
    // single username -> point to profile
    if (/^[a-z0-9_.-]+$/i.test(trimmed)) return `https://github.com/${trimmed}`;
    return trimmed;
  };

  const repoUrlNormalized = normalizeRepoUrl(repoUrl);
  const demoUrlNormalized = demoUrl && /^https?:\/\//i.test(String(demoUrl).trim()) ? String(demoUrl).trim() : demoUrl;

  const getTechColor = (tech) => {
    const colors = {
      'React': 'bg-blue-100 text-blue-800',
      'Node.js': 'bg-green-100 text-green-800',
      'Python': 'bg-yellow-100 text-yellow-800',
      'TypeScript': 'bg-indigo-100 text-indigo-800',
      'JavaScript': 'bg-amber-100 text-amber-800',
      'Vue.js': 'bg-emerald-100 text-emerald-800',
      'Angular': 'bg-red-100 text-red-800',
      'Next.js': 'bg-gray-100 text-gray-800',
      'Express': 'bg-purple-100 text-purple-800',
      'MongoDB': 'bg-teal-100 text-teal-800'
    };
    return colors?.[tech] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-background border border-border rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name={type === 'demo' ? 'ExternalLink' : 'Github'} size={20} className="text-primary" />
            <h2 className="text-xl font-semibold text-foreground">{type === 'demo' ? 'Project Demo' : 'Source Code'}: {project?.title}</h2>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} className="text-muted-foreground hover:text-foreground" />
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {type === 'demo' ? (
            <div className="p-6">
              {/* Project Image */}
              <div className="relative h-64 mb-6 rounded-lg overflow-hidden">
                <Image src={project?.image} alt={project?.imageAlt} className="w-full h-full object-cover" />
              </div>

              {/* Project Details */}
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">About This Project</h3>
                  <p className="text-muted-foreground leading-relaxed">{project?.fullDescription}</p>
                </div>

                {/* Technologies */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Technologies Used</h3>
                  <div className="flex flex-wrap gap-2">
                    {project?.technologies?.map((tech, index) => (
                      <span key={index} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${getTechColor(tech)}`}>{tech}</span>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Key Features</h3>
                  <ul className="space-y-2">
                    {project?.features?.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Metrics */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Project Impact</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-primary">{project?.stars}</div>
                      <div className="text-sm text-muted-foreground">GitHub Stars</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-primary">{project?.forks}</div>
                      <div className="text-sm text-muted-foreground">Forks</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-primary">{project?.users}</div>
                      <div className="text-sm text-muted-foreground">Active Users</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-primary">{project?.performance}</div>
                      <div className="text-sm text-muted-foreground">Performance</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button variant="default" iconName="ExternalLink" iconPosition="left" onClick={() => demoUrlNormalized && window.open(demoUrlNormalized, '_blank')}>View Live Demo</Button>
                  <Button variant="outline" iconName="Github" iconPosition="left" onClick={() => repoUrlNormalized && window.open(repoUrlNormalized, '_blank')}>View Source Code</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Repository Info */}
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Github" size={24} className="text-foreground" />
                    <div>
                      <h3 className="font-semibold text-foreground">{project?.title}</h3>
                      <p className="text-sm text-muted-foreground">{repoUrlNormalized}</p>
                    </div>
                  </div>
                  <Button variant="default" iconName="ExternalLink" iconPosition="left" onClick={() => repoUrlNormalized && window.open(repoUrlNormalized, '_blank')}>Open Repository</Button>
                </div>

                {/* Repository Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 border border-border rounded-lg">
                    <Icon name="Star" size={20} className="text-yellow-500 mx-auto mb-2" />
                    <div className="text-lg font-semibold text-foreground">{project?.stars}</div>
                    <div className="text-sm text-muted-foreground">Stars</div>
                  </div>
                  <div className="text-center p-4 border border-border rounded-lg">
                    <Icon name="GitFork" size={20} className="text-blue-500 mx-auto mb-2" />
                    <div className="text-lg font-semibold text-foreground">{project?.forks}</div>
                    <div className="text-sm text-muted-foreground">Forks</div>
                  </div>
                  <div className="text-center p-4 border border-border rounded-lg">
                    <Icon name="Eye" size={20} className="text-green-500 mx-auto mb-2" />
                    <div className="text-lg font-semibold text-foreground">{project?.watchers}</div>
                    <div className="text-sm text-muted-foreground">Watchers</div>
                  </div>
                  <div className="text-center p-4 border border-border rounded-lg">
                    <Icon name="GitCommit" size={20} className="text-purple-500 mx-auto mb-2" />
                    <div className="text-lg font-semibold text-foreground">{project?.commits}</div>
                    <div className="text-sm text-muted-foreground">Commits</div>
                  </div>
                </div>

                {/* Code Preview */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Code Preview</h3>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-sm text-gray-100"><code>{project?.codePreview}</code></pre>
                  </div>
                </div>

                {/* Recent Commits */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Recent Activity</h3>
                  <div className="space-y-3">
                    {project?.recentCommits?.map((commit, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 border border-border rounded-lg">
                        <Icon name="GitCommit" size={16} className="text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{commit?.message}</p>
                          <p className="text-xs text-muted-foreground">{commit?.date} • {commit?.author}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
